import React, { useCallback } from 'react';
import { ReactComponent as Reason } from 'assets/icons/appointmentsIcons/reason-goal.svg';
import { ReactComponent as Clock } from 'assets/icons/time.svg';
import { ReactComponent as Scheduled } from 'assets/icons/calendar.svg';
import { ReactComponent as Info } from 'assets/icons/info-gray.svg';
import { ReactComponent as Close } from 'assets/icons/iconClose.svg';
import {
  AppointmentRequestOutput,
  AppointmentRequestStatus,
  AppointmentRequestType,
  AppointmentTypeEnum,
} from 'graphql/generated/hasura';
import ButtonComponent from 'components/button/buttonComponent';
import {
  MESSAGES,
  MY_PATIENTS,
  MY_PATIENTS_BIO_INFO,
  MY_PATIENTS_BIO_INFO_PATIENT_INFO,
} from 'utilities/routes';
import { useNavigate } from 'react-router-dom';
import DateAppointment from '../upcoming-appointments/DateAppointment';
import { showTooltipMinChars } from '../upcoming-appointments/ReasonAndPaymentBlock';
import IconRow from '../upcoming-appointments/IconRow';
import { useGetProviderOfferingsData } from './useAppointmentRequests';
import { appointmentRenderInfo } from '../../interfaces';
import { twMerge } from 'tailwind-merge';
import {
  classNameRequestStatus,
  textRequestStatus,
  textTooltipStatus,
} from './helper';
import { formatDateForAppointment } from 'utilities/functions';
import { useDeleteAppointmentRequestMutation } from 'graphql/generated/remote-schema-hasura';
import CustomTooltip from '../../../../components/CustomTooltip';
import moment from 'moment';

const Card: React.FC<AppointmentRequestOutput> = ({
  patient,
  proposedDate,
  currentDate,
  id,
  reason,
  type: requestType,
  appointmentTypeId,
  status,
}) => {
  const navigate = useNavigate();
  const handlePatientClick = useCallback(() => {
    return navigate(
      `${MY_PATIENTS}/${patient?.id}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_INFO_PATIENT_INFO}`,
    );
  }, [patient, navigate]);

  const handleMessagesClick = useCallback(() => {
    return navigate(`${MESSAGES}/${patient?.id}`);
  }, [patient, navigate]);

  const [updateInAppNotificationMutation] = useDeleteAppointmentRequestMutation(
    {
      refetchQueries: ['GetProviderAppointmentRequests'],
      variables: {
        input: {
          appointmentRescheduleRequestId: id,
        },
      },
    },
  );

  const { providerTypes } = useGetProviderOfferingsData();
  const currentType = providerTypes.find(({ id }) => id === appointmentTypeId);
  const type = (currentType?.name ?? ' ')
    .split(' ')?.[0]
    ?.toUpperCase() as AppointmentTypeEnum;
  const { icon: Icon, label } =
    appointmentRenderInfo[type || AppointmentTypeEnum.Chat];

  const totalTime =
    +(currentType?.duration || 0) + (currentType?.paddingAfter || 0);

  const statusClassName = classNameRequestStatus[status];
  const labelStatus = textRequestStatus[status];
  const tooltip = textTooltipStatus[status];
  const IconComponent = Icon || (() => null);

  const isApproveWithNewTime =
    requestType === AppointmentRequestType.Book &&
    currentDate !== proposedDate &&
    status === AppointmentRequestStatus.Approved;

  return (
    <div
      className="max-desktop:hidden w-full"
      data-testid="upcoming-appointment-card-desktop"
    >
      <div className="flex items-center desktop:px-30 px-5 py-4 border border-transparent border-b-gray-opacity-15 bg-neutral-100 rounded-tr-xl rounded-tl-xl">
        <span className="text-title font-semibold text-neutral-800 whitespace-nowrap">
          {requestType === AppointmentRequestType.Reschedule
            ? 'Reschedule appointment with'
            : 'Request for appointment with'}
        </span>
        <ButtonComponent
          type="underline"
          onClick={handlePatientClick}
          noUpperCase
          testID={'provider-name'}
          containerClassName="!text-h5"
          className="ml-1 !text-clc-blue !text-h5 whitespace-nowrap"
        >
          {patient?.SENSITIVE_firstname} {patient?.SENSITIVE_lastname}
        </ButtonComponent>

        <span
          className={twMerge(
            'mx-3 text-sm font-semibold border px-3 min-w-[6rem] text-center rounded-full',
            statusClassName,
          )}
          data-testid={`appointment-status-${status}`}
        >
          {status}
        </span>

        <p className="text-med-gray-3 items-center font-semibold text-sm flex gap-2.5 ml-30">
          <Info
            data-tooltip-id={`request-status-${id}`}
            className={twMerge(
              'w-5 h-5',
              isApproveWithNewTime && 'fill-amber-border',
            )}
          />
          <p className={twMerge(isApproveWithNewTime && 'hidden')}>
            {labelStatus}
          </p>
          <p
            className={twMerge(
              'hidden',
              isApproveWithNewTime && 'inline-block text-amber-border',
            )}
          >
            Patient accepted the appointment request and selected a different
            time:{' '}
            <span className="font-bold">
              {formatDateForAppointment(currentDate as string)}
            </span>
          </p>
        </p>

        <ButtonComponent
          type="icon"
          testID="close"
          className={twMerge(
            'p-0 ml-auto !bg-transparent',
            status === AppointmentRequestStatus.Pending && 'hidden',
          )}
          onClick={() => updateInAppNotificationMutation()}
          Icon={() => (
            <Close className="w-[24px] h-[24px] stroke-neutral-800" />
          )}
        />
      </div>

      <div className="grid grid-cols-15 gap-30 p-5 desktop:px-30 pb-5 pt-2.5 border-b-gray-opacity-15 bg-neutral-100 rounded-br-xl rounded-bl-xl">
        <div className="col-span-2">
          <DateAppointment startDate={currentDate || proposedDate} />
        </div>
        <div className="col-span-3 flex justify-start">
          <div className="flex flex-col justify-center gap-2.5">
            <IconRow
              icon={<Clock className="fill-neutral-800" />}
              text={`${totalTime ?? 0} min`}
            />
            <IconRow
              icon={<IconComponent className="fill-neutral-800 w-[20px]" />}
              text={label}
              testId={`appointment-type-${type}`}
            />
          </div>
        </div>
        <div className="col-span-8 flex justify-start">
          <div
            className={twMerge(
              'flex flex-col justify-center gap-2.5 w-full',
              requestType === AppointmentRequestType.Reschedule && 'hidden',
            )}
          >
            <IconRow
              icon={<Reason className="min-w-[1.25rem]" />}
              tooltipId={id}
              text={reason}
              showTooltip={(reason?.length || 0) >= showTooltipMinChars}
            />
            <IconRow
              icon={<Scheduled className="fill-neutral-800" />}
              text={`${moment(currentDate || proposedDate).format(
                'hh:mm',
              )} - ${moment(currentDate || proposedDate)
                .add(+(totalTime || 0), 'minutes')
                .format('hh:mm')}`}
            />
          </div>
          <div
            className={twMerge(
              'flex flex-col justify-center gap-2.5 w-full',
              requestType === AppointmentRequestType.Book && 'hidden',
            )}
          >
            <IconRow
              icon={<Clock className="fill-neutral-800" />}
              text={
                currentDate &&
                `Current appointment date & time: ${formatDateForAppointment(
                  currentDate,
                )}`
              }
            />
            <IconRow
              icon={<Clock className="fill-neutral-800" />}
              text={
                proposedDate &&
                `Suggested new date & time: ${formatDateForAppointment(
                  proposedDate,
                )}`
              }
            />
          </div>
        </div>
        <div
          className={twMerge(
            'col-span-2 flex justify-end',
            status !== AppointmentRequestStatus.Pending && 'hidden',
          )}
        >
          <ButtonComponent
            testID="send"
            className="whitespace-nowrap h-fit my-auto"
            onClick={handleMessagesClick}
          >
            Send a message
          </ButtonComponent>
        </div>
      </div>

      <CustomTooltip id={`request-status-${id}`} content={tooltip} />
    </div>
  );
};

export default Card;
